import { component } from 'picoapp';
import Drawer from '../../../js/utils/drawer';

export default component((node) => {
  const closeBtn = node.querySelector('.js-sizeGuideDrawerCloseBtn');
  const brandSelect = node.querySelector('.js-brandSizeTableSelect');
  const brandTables = node.querySelectorAll('.js-brandSizeTable');
  const accordionItems = node.querySelectorAll('.js-sizeGuideAccordion');
  const nestedToggles = node.querySelectorAll('.js-sizeGuideNestedToggle');

  const replayFadeIn = (el) => {
    if (!el) return;
    el.style.animation = 'none';
    void el.offsetWidth;
    el.style.animation = '';
  };

  const setAccordionOpen = (target, open) => {
    const btn = target.querySelector('.js-sizeGuideAccordionBtn');
    const content = target.querySelector('.js-sizeGuideAccordionContent');

    if (!btn || !content) return;

    content.hidden = !open;
    btn.classList.toggle('harmonica-item__button--active', open);
    btn.setAttribute('aria-expanded', open ? 'true' : 'false');

    if (open) {
      replayFadeIn(content.querySelector('.harmonica-item__content'));
    }
  };

  const setNestedOpen = (btn, open) => {
    const content = btn.nextElementSibling;
    if (!content) return;

    content.hidden = !open;
    btn.classList.toggle('harmonica-item__button--active', open);
    btn.setAttribute('aria-expanded', open ? 'true' : 'false');

    if (open) {
      replayFadeIn(content);
    }
  };

  const closeAllAccordions = () => {
    accordionItems.forEach((item) => setAccordionOpen(item, false));
    nestedToggles.forEach((btn) => setNestedOpen(btn, false));
  };

  const initDrawer = () => {
    document.body.appendChild(node);
    const drawer = Drawer(node);

    document.querySelectorAll('.js-sizeGuideBtn').forEach((btn) => {
      if (!btn.classList.contains('js-yourSizeBtn')) {
        btn.hidden = false;
      }
      btn.addEventListener('click', (event) => {
        event.stopPropagation();
        drawer.open();
      });
    });

    node.addEventListener('click', (event) => event.stopPropagation());
    closeBtn?.addEventListener('click', () => drawer.close());
    drawer.on('open', () => {
      closeAllAccordions();
      scheduleChartTableLayout();
    });
  };

  const initAccordions = () => {
    accordionItems.forEach((item) => {
      const btn = item.querySelector('.js-sizeGuideAccordionBtn');

      btn?.addEventListener('click', () => {
        const content = item.querySelector('.js-sizeGuideAccordionContent');
        const willOpen = content?.hidden;

        accordionItems.forEach((other) => setAccordionOpen(other, false));

        if (willOpen) {
          setAccordionOpen(item, true);
        }

        scheduleChartTableLayout();
      });
    });

    nestedToggles.forEach((btn) => {
      btn.addEventListener('click', () => {
        const content = btn.nextElementSibling;
        if (!content) return;

        setNestedOpen(btn, content.hidden);
      });
    });
  };

  const labelKey = (label) => `product.size_guide.${String(label || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_|_$/g, '')}`;

  const getChartLabel = (label, labels) => {
    const translated = labels[label] || labels[labelKey(label).replace('product.size_guide.', '')];
    return translated || label;
  };

  const parseChartData = (raw) => {
    if (!raw) return null;

    let data = raw;
    if (typeof data === 'string') {
      try {
        data = JSON.parse(data);
      } catch (error) {
        return null;
      }
    }

    if (typeof data === 'string') {
      try {
        data = JSON.parse(data);
      } catch (error) {
        return null;
      }
    }

    return data;
  };

  const renderChartFallback = () => {
    node.querySelectorAll('[data-size-guide-chart]').forEach((root) => {
      if (root.querySelector('table')) return;

      const data = parseChartData(root.querySelector('.js-sizeGuideChartData')?.textContent);
      const fits = data?.fits;
      if (!Array.isArray(fits) || fits.length === 0) return;

      const labels = {};
      root.querySelectorAll('[data-size-guide-label]').forEach((el) => {
        labels[el.dataset.sizeGuideLabel] = el.textContent;
      });

      const namedFits = fits.filter((fit) => fit.fit);
      const fragment = document.createDocumentFragment();

      const fitId = (fit, index) => `SizeGuideFitFallback-${index}-${String(fit.fit || 'default')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '')}`;

      if (namedFits.length > 1) {
        const tabs = document.createElement('div');
        tabs.className = 'product__size-guide-fits js-sizeGuideFitTabs';
        tabs.setAttribute('role', 'tablist');
        namedFits.forEach((fit) => {
          const index = fits.indexOf(fit);
          const panelId = fitId(fit, index);
          const isFirst = fit === namedFits[0];
          const button = document.createElement('button');
          button.type = 'button';
          button.id = `${panelId}-tab`;
          button.className = `product__size-guide-fits-btn js-sizeGuideFitTab${isFirst ? ' is-active' : ''}`;
          button.dataset.fit = fit.fit;
          button.setAttribute('role', 'tab');
          button.setAttribute('aria-selected', isFirst ? 'true' : 'false');
          button.setAttribute('aria-controls', panelId);
          button.textContent = fit.fit;
          tabs.appendChild(button);
        });
        fragment.appendChild(tabs);
      }

      fits.forEach((fit, index) => {
        const panelId = fitId(fit, index);
        const group = document.createElement('div');
        group.id = panelId;
        group.className = 'js-sizeGuideFitGroup';
        group.dataset.fit = fit.fit || 'default';
        if (namedFits.length > 1) {
          group.setAttribute('role', 'tabpanel');
          group.setAttribute('aria-labelledby', `${panelId}-tab`);
        }
        if (namedFits.length > 1 && index > 0) group.hidden = true;

        const wrap = document.createElement('div');
        wrap.className = 'product__size-guide-table-wrap';
        const table = document.createElement('table');
        table.className = 'product__size-guide-table';
        const columns = fit.columns || [];
        table.innerHTML = `<thead><tr><th></th>${columns.map((col) => `<th>${col}</th>`).join('')}</tr></thead><tbody>${
          (fit.rows || []).map((row) => `<tr><th>${getChartLabel(row.label, labels)}</th>${
            (row.values || []).map((value) => `<td>${value}</td>`).join('')
          }</tr>`).join('')
        }</tbody>`;
        wrap.appendChild(table);
        group.appendChild(wrap);
        fragment.appendChild(group);
      });

      root.appendChild(fragment);
    });
  };

  const initFitTabs = () => {
    node.querySelectorAll('[data-size-guide-chart]').forEach((root) => {
      const tabs = root.querySelectorAll('.js-sizeGuideFitTab');
      const groups = root.querySelectorAll('.js-sizeGuideFitGroup');

      tabs.forEach((tab) => {
        tab.addEventListener('click', () => {
          tabs.forEach((other) => {
            const isActive = other === tab;
            other.classList.toggle('is-active', isActive);
            other.setAttribute('aria-selected', isActive ? 'true' : 'false');
          });

          groups.forEach((group) => {
            group.hidden = group.dataset.fit !== tab.dataset.fit;
          });

          scheduleChartTableLayout();
        });
      });
    });
  };

  const syncChartColumnWidths = () => {
    node.querySelectorAll('[data-size-guide-chart]').forEach((root) => {
      const tables = [...root.querySelectorAll('.product__size-guide-table')];
      if (tables.length === 0) return;

      tables.forEach((table) => {
        table.style.tableLayout = 'auto';
        table.style.width = 'auto';
        table.querySelectorAll('th, td').forEach((cell) => {
          cell.style.width = '';
          cell.style.minWidth = '';
          cell.style.maxWidth = '';
        });
      });

      const visibleTable = tables.find((table) => !table.closest('[hidden]')) || tables[0];
      const labelCells = [...visibleTable.querySelectorAll('tbody th')];
      if (labelCells.length === 0) return;

      let labelWidth = 0;
      labelCells.forEach((cell) => {
        labelWidth = Math.max(labelWidth, Math.ceil(cell.getBoundingClientRect().width));
      });

      if (!labelWidth) return;

      labelWidth = Math.min(90, Math.max(60, labelWidth));
      const availableWidth = Math.floor(
        visibleTable.closest('.product__size-guide-table-wrap')?.getBoundingClientRect().width || 0,
      );

      tables.forEach((table) => {
        const columnCount = table.querySelectorAll('thead th').length;
        if (!columnCount) return;

        const cellWidth = availableWidth
          ? Math.max(labelWidth, Math.floor(availableWidth / columnCount))
          : labelWidth;
        const tableWidth = cellWidth * columnCount;
        const fillsContainer = availableWidth > 0 && tableWidth <= availableWidth;

        table.style.tableLayout = 'fixed';
        table.style.width = fillsContainer ? '100%' : `${tableWidth}px`;
        table.querySelectorAll('th, td').forEach((cell) => {
          cell.style.width = `${cellWidth}px`;
          cell.style.minWidth = `${cellWidth}px`;
          cell.style.maxWidth = fillsContainer ? 'none' : `${cellWidth}px`;
        });

        const wrap = table.closest('.product__size-guide-table-wrap');
        if (wrap) {
          wrap.classList.toggle('is-scrollable', wrap.scrollWidth > wrap.clientWidth + 1);
        }
      });
    });
  };

  const initChartTableDrag = () => {
    const DRAG_THRESHOLD = 5;

    node.querySelectorAll('.product__size-guide-table-wrap').forEach((scrollContainer) => {
      let isDown = false;
      let startX;
      let scrollLeft;
      let hasMoved = false;

      scrollContainer.addEventListener('mousedown', (event) => {
        if (scrollContainer.scrollWidth <= scrollContainer.clientWidth) return;

        isDown = true;
        hasMoved = false;
        scrollContainer.classList.add('is-dragging');
        startX = event.pageX;
        scrollLeft = scrollContainer.scrollLeft;
        event.preventDefault();
      });

      const stopDrag = () => {
        isDown = false;
        scrollContainer.classList.remove('is-dragging');
      };

      scrollContainer.addEventListener('mouseleave', stopDrag);
      scrollContainer.addEventListener('mouseup', stopDrag);

      scrollContainer.addEventListener('mousemove', (event) => {
        if (!isDown) return;

        const walk = event.pageX - startX;
        if (Math.abs(walk) > DRAG_THRESHOLD) hasMoved = true;

        event.preventDefault();
        scrollContainer.scrollLeft = scrollLeft - walk * 2;
      });

      scrollContainer.addEventListener('click', (event) => {
        if (!hasMoved) return;
        event.preventDefault();
        event.stopPropagation();
      }, true);
    });
  };

  const markWrappedChartLabels = () => {
    node.querySelectorAll('.product__size-guide-table th').forEach((cell) => {
      if (cell.closest('[hidden]')) return;

      cell.classList.remove('is-wrapped');

      const range = document.createRange();
      range.selectNodeContents(cell);
      const textRects = [...range.getClientRects()].filter((rect) => rect.width > 1 && rect.height > 1);
      const wraps = textRects.length > 1;

      cell.classList.toggle('is-wrapped', wraps);
    });
  };

  const scheduleChartTableLayout = () => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        syncChartColumnWidths();
        markWrappedChartLabels();
      });
    });
  };

  const initBrandSelect = () => {
    if (!brandSelect) return;

    brandSelect.addEventListener('change', () => {
      brandTables.forEach((table) => {
        table.hidden = table.dataset.brandName !== brandSelect.value;
      });
    });
  };

  initDrawer();
  initAccordions();
  renderChartFallback();
  initFitTabs();
  initBrandSelect();
  initChartTableDrag();
});
